import { useEffect, useState } from "react";
import { X, Search, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function MediaLibraryModal({ onSelect, onClose }) {
    const [files, setFiles] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState("");
    const [activeQuery, setActiveQuery] = useState("");

    const load = (q = "") => {
        setLoading(true);
        setActiveQuery(q);
        api.get("/admin/files", { params: { search: q, limit: 100, skip: 0 } })
            .then((r) => {
                setFiles(r.data.files || []);
                setTotal(r.data.total || 0);
            })
            .catch(() => toast.error("Failed to load media library"))
            .finally(() => setLoading(false));
    };

    const loadMore = () => {
        setLoadingMore(true);
        api.get("/admin/files", { params: { search: activeQuery, limit: 100, skip: files.length } })
            .then((r) => {
                setFiles((prev) => {
                    const seen = new Set(prev.map((x) => x.id));
                    return [...prev, ...(r.data.files || []).filter((x) => !seen.has(x.id))];
                });
                setTotal(r.data.total || 0);
            })
            .catch(() => toast.error("Failed to load more images"))
            .finally(() => setLoadingMore(false));
    };

    useEffect(() => {
        load();
    }, []);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        load(search);
    };

    const remove = async (e, file) => {
        e.stopPropagation();
        if (!window.confirm(`Remove "${file.original_filename || file.storage_path}" from the library?`)) return;
        try {
            await api.delete(`/admin/files/${file.id}`);
            setFiles((f) => f.filter((x) => x.id !== file.id));
            setTotal((t) => Math.max(0, t - 1));
            toast.success("Removed");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to remove");
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
            style={{ zIndex: 100 }}
            onClick={onClose}
            data-testid="media-library-modal"
        >
            <div
                className="bg-white w-full max-w-4xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b border-[#EAE3DE]">
                    <h3 className="font-display text-xl">Media Library</h3>
                    <button type="button" onClick={onClose} aria-label="Close" data-testid="media-library-close">
                        <X size={20} />
                    </button>
                </div>

                {/* Not a <form> — this modal can be opened from inside other admin forms
                    (Settings, Cakes, etc.), and a nested <form> is invalid HTML that
                    breaks the parent form's submit behavior. */}
                <div className="p-4 border-b border-[#EAE3DE] flex gap-2">
                    <input
                        type="text"
                        placeholder="Search filenames…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                onSearchSubmit(e);
                            }
                        }}
                        className="flex-1 px-4 py-2 border border-[#EAE3DE] focus:outline-none focus:border-blush text-sm"
                        data-testid="media-library-search"
                    />
                    <button type="button" onClick={onSearchSubmit} className="btn-pink inline-flex items-center gap-2">
                        <Search size={14} /> Search
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <p className="text-sm text-ink-soft">Loading…</p>
                    ) : files.length === 0 ? (
                        <p className="text-sm text-ink-soft">No images found.</p>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                            {files.map((f) => {
                                const absolute = f.url.startsWith("http") ? f.url : `${BACKEND_URL}${f.url}`;
                                return (
                                    <div
                                        key={f.id}
                                        className="relative group aspect-square overflow-hidden bg-[#F9F5F2] border border-[#EAE3DE] cursor-pointer"
                                        onClick={() => onSelect(absolute)}
                                        data-testid={`media-library-item-${f.id}`}
                                    >
                                        <img src={absolute} alt={f.original_filename || ""} className="w-full h-full object-cover" loading="lazy" />
                                        <button
                                            type="button"
                                            onClick={(e) => remove(e, f)}
                                            className="absolute top-1 right-1 w-6 h-6 bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                                            aria-label="Remove from library"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {!loading && files.length > 0 && (
                        <div className="mt-4 flex items-center justify-center gap-4 text-xs text-ink-soft">
                            <span data-testid="media-library-count">Showing {files.length} of {total}</span>
                            {files.length < total && (
                                <button type="button" onClick={loadMore} disabled={loadingMore} className="btn-pink disabled:opacity-60" data-testid="media-library-load-more">
                                    {loadingMore ? "Loading…" : "Load more"}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
